import { useEffect } from "react";
import { useSite } from "@/context/SiteContext";

const PRIVATE = /^\/(account|checkout|payment|login|register|forgot-password|reset-password|review|unsubscribe|track|search|admin)(\/|$)/;
const BRAND = "Somnio.Co";

function setTag(attr: string, key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const abs = (u: string) => (!u || /^https?:\/\//.test(u) ? u : window.location.origin + u);
export const withSuffix = (t: string, suffix: string) => (!t || t.toLowerCase().includes(BRAND.toLowerCase()) ? t : t + suffix);
export const plain = (s = "") => s.replace(/[#*_>[\]]/g, "").replace(/\s+/g, " ").trim();

type Opts = { image?: string; noindex?: boolean; type?: string };

export function useMeta(title?: string, description?: string, jsonLd?: any, opts: Opts = {}) {
  const { site } = useSite();
  const seo = site?.seo || {};
  useEffect(() => {
    if (!title || !site) return;
    const full = withSuffix(title, seo.suffix ?? ` | ${BRAND}`);
    const desc = plain(description || seo.description || "").slice(0, 300);
    const url = window.location.origin + window.location.pathname;
    const img = abs(opts.image || seo.image || "");
    document.title = full;
    setTag("name", "description", desc);
    [["og:title", full], ["og:description", desc], ["og:url", url], ["og:type", opts.type || "website"], ["og:site_name", site.brand || BRAND], ["og:image", img]]
      .forEach(([k, v]) => setTag("property", k, v));
    [["twitter:card", "summary_large_image"], ["twitter:title", full], ["twitter:description", desc], ["twitter:image", img]].forEach(([k, v]) => setTag("name", k, v));
    setTag("name", "robots", opts.noindex || PRIVATE.test(window.location.pathname) ? "noindex, follow" : "index, follow");
    if (seo.google_verification) setTag("name", "google-site-verification", seo.google_verification);
    else document.head.querySelector('meta[name="google-site-verification"]')?.remove();
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;
    let script: HTMLScriptElement | null = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => { if (script) script.remove(); };
  }, [title, description, JSON.stringify(jsonLd || null), opts.image, opts.noindex, opts.type, site]);
}
